import { AppstoreAddOutlined, ArrowLeftOutlined, CarOutlined, CheckCircleTwoTone, CommentOutlined, DollarOutlined, DropboxOutlined, ExclamationCircleTwoTone, InfoCircleOutlined, SaveOutlined, SolutionOutlined, TagsOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Form, Input, message, Modal, Row, Select, Space, Table, Tabs, Tooltip, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../../services/api";
import { confirmBooking, createBooking } from "../../../services/bookingApi";
import useTaxStore from "../../../store/taxStore";
import { IBookingnew } from "../../../types/bookingnew";
import SearchNcm from "../Search/search-ncm";

const { Title, Text, Paragraph } = Typography;

const TAB_CONFIG = [
  {
    key: "1",
    label: "General Details",
    icon: <SolutionOutlined />,
    subtitle: "Preencha as informações básicas do booking.",
  },
  {
    key: "2",
    label: "Parties",
    icon: <TeamOutlined />,
    subtitle: "Informe as partes envolvidas no processo.",
  },
  {
    key: "3",
    label: "References",
    icon: <TagsOutlined />,
    subtitle: "Inclua referências de embarque e compra.",
  },
  {
    key: "4",
    label: "Transport",
    icon: <CarOutlined />,
    subtitle: "Detalhes logísticos do transporte.",
  },
  {
    key: "5",
    label: "Containers",
    icon: <AppstoreAddOutlined />,
    subtitle: "Quantidade e tipo dos containers.",
  },
  {
    key: "6",
    label: "Cargo",
    icon: <DropboxOutlined />,
    subtitle: "Informações sobre a mercadoria transportada.",
  },
  {
    key: "7",
    label: "Payments",
    icon: <DollarOutlined />,
    subtitle: "Condições e detalhes de pagamento.",
  },
  {
    key: "8",
    label: "Comments",
    icon: <CommentOutlined />,
    subtitle: "Observações adicionais e notificações.",
  },
];

/** Tipagem leve para as taxas vindas do store */
type StoreTax = {
  category?: string; // nome exibido (ex.: "Terminal handling charge [THC]")
  title?: string; // se o store usar "title"
  description?: string; // se o store já trouxer description
  value: number;
  currency: string;
  applicability?: "C" | "B" | string; // C=por contêiner, B=por BL (se você usar)
};

/** Empacota taxas do store => { description, category, value, currency, applicability } */
function packTaxes(origin: StoreTax[] = [], freight: StoreTax[] = []) {
  const norm = (t: StoreTax) => {
    const desc = (t.description || t.title || t.category || "").toString();
    return {
      description: desc, // <-- novo campo
      value: Number(t.value || 0),
      currency: (t.currency || "").toUpperCase(),
      applicability: (t.applicability as any) ?? "C",
    };
  };

  const originPacked = origin.map((t) => ({
    ...norm(t),
    category: "Taxas locais da origem", // <-- novo campo
  }));

  const freightPacked = freight.map((t) => ({
    ...norm(t),
    category: "Frete e taxas que acompanham frete", // <-- novo campo
  }));

  return [...originPacked, ...freightPacked];
}

const InfoPedido = (props: any) => {
  const [form] = Form.useForm();
  const [qtdContainers, setQtdContainers] = useState<number>(1);
  const [empresaEmbarcador, setEmpresaEmbarcador] = useState("");
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    message: "",
  });
  const [tabError, setTabError] = useState<Record<string, boolean>>({});
  const [activeKey, setActiveKey] = useState("1");
  const [savedBookingId, setSavedBookingId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { groupedTaxes } = useTaxStore();

  // As taxas JÁ categorizadas pelo store
  const originTaxes: StoreTax[] = groupedTaxes["Taxas locais da origem"] || [];
  const freightTaxes: StoreTax[] =
    groupedTaxes["Frete e taxas que acompanham frete"] || [];

  useEffect(() => {
    api
      .post("/user/find_user", { email: localStorage.getItem("user_email") })
      .then((resp) => {
        setEmpresaEmbarcador(resp.data.user.enterpriseName);
      });
  }, []);

  // Preenchimento automático dos campos fixos
  useEffect(() => {
    form.setFieldsValue({
      carrier: props.armador,
      forwarder: empresaEmbarcador,
      placeCarrierReceipt: props.porto_embarque,
      placeCarrierDelivery: props.porto_descarga,
      earlyDepartureDate: formataData(props.data_embarque),
      latestDeliveryDate: formataData(props.data_chegada),
      qtdContainers,
      tipo_container: props.tipo_container,
      moveType: "PortToPort",
    });
    // eslint-disable-next-line
  }, [
    empresaEmbarcador,
    props.armador,
    props.porto_embarque,
    props.porto_descarga,
    props.data_embarque,
    props.data_chegada,
    props.tipo_container,
    qtdContainers,
  ]);

  function formataData(date?: string) {
    if (!date) return "";
    const dateDate = date.split("/");
    return (
      (dateDate[0]?.length >= 2 ? dateDate[0] : "0" + dateDate[0]) +
      "/" +
      dateDate[1] +
      "/" +
      dateDate[2]
    );
  }

  // TAB error management
  const setTabsWithErrors = (fieldsError: any[] = []) => {
    const newTabError: Record<string, boolean> = {};
    for (const t of TAB_CONFIG) {
      if (
        fieldsError.some((err) =>
          (FIELDS_BY_TAB[t.key] || []).includes(err.name[0])
        )
      ) {
        newTabError[t.key] = true;
      }
    }
    setTabError(newTabError);
    const firstWithError = TAB_CONFIG.find((t) => newTabError[t.key]);
    if (firstWithError) setActiveKey(firstWithError.key);
  };

  // Submit handlers
  const validateAndSubmit = async (
    submitFn: (v: any) => void | Promise<void>
  ) => {
    try {
      const values = await form.validateFields();
      setTabError({});
      await submitFn(values);
    } catch (err: any) {
      if (err.errorFields) setTabsWithErrors(err.errorFields);
      message.error("Preencha todos os campos obrigatórios.");
    }
  };

  const handleSave = async (values: any) => {
    try {
      const data: Partial<IBookingnew> = {
        ...values,
        armador: props.armador,
        id_armador: props.id_armador,
        porto_embarque: props.porto_embarque,
        quantidade_containers: values.qtdContainers || qtdContainers,
        porto_descarga: props.porto_descarga,
        data_embarque: props.data_embarque,
        tipo_container: props.tipo_container,
        // IMPORTANT: taxas empacotadas com description + category
        taxas: packTaxes(originTaxes, freightTaxes),
      };

      const result = await createBooking(data);
      setSavedBookingId(result._id);
      setModal({
        visible: true,
        title: "Sucesso",
        message: "Booking salvo com sucesso! Confirme para finalizar.",
      });
      message.success("Booking salvo!");
    } catch (err: any) {
      setModal({
        visible: true,
        title: "Erro",
        message: err?.response?.data?.error || "Erro ao salvar booking",
      });
      message.error("Falha ao salvar booking");
    }
  };

  const handleConfirm = async (values: any) => {
    try {
      let idToConfirm = savedBookingId;

      if (!idToConfirm) {
        const data: Partial<IBookingnew> = {
          ...values,
          armador: props.armador,
          id_armador: props.id_armador,
          quantidade_containers: values.qtdContainers || qtdContainers,
          porto_embarque: props.porto_embarque,
          porto_descarga: props.porto_descarga,
          data_embarque: props.data_embarque,
          tipo_container: props.tipo_container,
          // IMPORTANT: taxas empacotadas com description + category
          taxas: packTaxes(originTaxes, freightTaxes),
        };
        const result = await createBooking(data);
        idToConfirm = result._id;
      }

      await confirmBooking(idToConfirm!);
      setModal({
        visible: true,
        title: "Booking Confirmado",
        message:
          "O booking foi solicitado ao armador, gentileza aguardar o Booking Number que sera enviado no seu e-mail.",
      });
      message.success("Booking confirmado!");
      setTimeout(() => navigate("/pedido_resultado"), 1200);
    } catch (err: any) {
      setModal({
        visible: true,
        title: "Erro",
        message: err?.response?.data?.error || "Erro ao confirmar booking",
      });
      message.error("Falha ao confirmar booking");
    }
  };

  // ---------- UI ----------
  return (
    <div
      style={{ background: "#f4f8fb", padding: "32px 0", minHeight: "100vh" }}
    >
      <Row gutter={32} justify="center">
        <Col xs={24} md={15} xl={15}>
          <Card
            style={{
              borderRadius: 16,
              boxShadow: "0 3px 24px #0066cc18",
              padding: 0,
              background: "#fff",
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ padding: "32px 32px 0 32px" }}>
              <Title level={2} style={{ color: "#0066cc", marginBottom: 0 }}>
                <SolutionOutlined style={{ marginRight: 10 }} />
                Novo Booking
              </Title>
              <Text type="secondary" style={{ fontSize: 16 }}>
                Organize o booking em etapas. Preencha todas as abas para
                prosseguir.
              </Text>
              <Divider style={{ marginTop: 12, marginBottom: 24 }} />
            </div>

            <div style={{ padding: "0 32px 24px 32px" }}>
              <Form
                form={form}
                layout="vertical"
                style={{ marginTop: 8 }}
                size="middle"
              >
                <Tabs
                  defaultActiveKey="1"
                  activeKey={activeKey}
                  onChange={setActiveKey}
                  type="card"
                  tabBarGutter={8}
                  moreIcon={null}
                >
                  {TAB_CONFIG.map((tab) => (
                    <Tabs.TabPane
                      tab={
                        <span>
                          {tab.icon}
                          <span
                            style={
                              tabError[tab.key] ? { color: "red" } : undefined
                            }
                          >
                            {tab.label}
                            {tabError[tab.key] && (
                              <ExclamationCircleTwoTone
                                twoToneColor="#f5222d"
                                style={{ marginLeft: 6 }}
                              />
                            )}
                          </span>
                        </span>
                      }
                      key={tab.key}
                    >
                      <Title
                        level={4}
                        style={{ marginBottom: 0, marginTop: 10 }}
                      >
                        {tab.icon} {tab.label}
                      </Title>
                      <Text
                        type="secondary"
                        style={{ marginBottom: 12, display: "block" }}
                      >
                        {tab.subtitle}
                      </Text>
                      <Divider style={{ margin: "8px 0 24px" }} />

                      {/* TAB 1 */}
                      {tab.key === "1" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              label={
                                <span>
                                  Carrier / NVOCC&nbsp;
                                  <Tooltip title="Transportadora ou agente do booking">
                                    <InfoCircleOutlined />
                                  </Tooltip>
                                </span>
                              }
                              name="carrier"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Selecione o agente responsável."
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Contract Number*"
                              name="contractNumber"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Informe o número do contrato."
                            >
                              <Input placeholder="Digite o número do contrato" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Booking Office"
                              name="bookingOffice"
                              help="Informe o escritório responsável."
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* TAB 2 */}
                      {tab.key === "2" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item label="Shipper" name="shipper">
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Forwarder" name="forwarder">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Consignee" name="consignee">
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* TAB 3 */}
                      {tab.key === "3" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              label="Shipper Reference Number"
                              name="shipperRefNumber"
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Forwarder's Reference Number"
                              name="forwardRefNumber"
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Purchase Order Number"
                              name="purchaseOrderNumber"
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* TAB 4 */}
                      {tab.key === "4" && (
                        <>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item
                                label="Move Type"
                                name="moveType"
                                rules={[
                                  { required: true, message: "Obrigatório" },
                                ]}
                                help="Tipo de operação do transporte."
                              >
                                <Select>
                                  <Select.Option value="PortToPort">
                                    Port, Ramp, or CY to Port, Ramp, or CY
                                  </Select.Option>
                                  <Select.Option value="DoorToPort">
                                    Door to port, Ramp, or Cy
                                  </Select.Option>
                                  <Select.Option value="DoorToDoor">
                                    Door to Door
                                  </Select.Option>
                                  <Select.Option value="PortToDoor">
                                    Port Ramp, or Cy to Door
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Place of Carrier Receipt"
                                name="placeCarrierReceipt"
                                rules={[
                                  { required: true, message: "Obrigatório" },
                                ]}
                              >
                                <Input disabled />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Early Departure Date"
                                name="earlyDepartureDate"
                                rules={[
                                  { required: true, message: "Obrigatório" },
                                ]}
                              >
                                <Input disabled />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}></Col>
                            <Col span={8}>
                              <Form.Item
                                label="Place of Carrier Delivery"
                                name="placeCarrierDelivery"
                                rules={[
                                  { required: true, message: "Obrigatório" },
                                ]}
                              >
                                <Input disabled />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                label="Latest Delivery Date"
                                name="latestDeliveryDate"
                                rules={[
                                  { required: true, message: "Obrigatório" },
                                ]}
                              >
                                <Input disabled />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* TAB 5 */}
                      {tab.key === "5" && (
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              label="Quantidade de Containers"
                              name="qtdContainers"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Defina a quantidade necessária de containers."
                            >
                              <Input
                                type="number"
                                min={1}
                                value={qtdContainers}
                                onChange={(e) =>
                                  setQtdContainers(Number(e.target.value))
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col span={16}>
                            <Form.Item
                              label="Tipo de Container"
                              name="tipo_container"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Tipo de container selecionado para o booking."
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* TAB 6 */}
                      {tab.key === "6" && (
                        <Form.Item
                          label="Digite parte do nome da mercadoria"
                          name="mercadoria"
                          rules={[{ required: true, message: "Obrigatório" }]}
                          help="Utilize a busca inteligente para selecionar o produto."
                        >
                          <SearchNcm />
                        </Form.Item>
                      )}

                      {/* TAB 7 */}
                      {tab.key === "7" && (
                        <Row gutter={16}>
                          <Col span={6}>
                            <Form.Item
                              label="Charge Type*"
                              name="paymentChargeType"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Tipo de cobrança do pagamento."
                            >
                              <Select>
                                <Select.Option value="Additional">
                                  Additional Charges
                                </Select.Option>
                                <Select.Option value="OceanFreight">
                                  Basic Freight
                                </Select.Option>
                                <Select.Option value="DestinationHaulage">
                                  Destination Haulage Charges
                                </Select.Option>
                                <Select.Option value="DetinationTerminalHandling">
                                  Destination Port Charges
                                </Select.Option>
                                <Select.Option value="OriginTerminalHandling">
                                  Origin Port Charges
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              label="Payment Term*"
                              name="paymentTerm"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Selecione a condição de pagamento."
                            >
                              <Select>
                                <Select.Option value="Prepaid">
                                  Pre-paid
                                </Select.Option>
                                <Select.Option value="Collect">
                                  Collect
                                </Select.Option>
                                <Select.Option value="PayableElsehere">
                                  Payable Elsewhere
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              label="Payer*"
                              name="payer"
                              rules={[
                                { required: true, message: "Obrigatório" },
                              ]}
                              help="Quem será o pagador?"
                            >
                              <Select>
                                <Select.Option value="Booker">
                                  Booker
                                </Select.Option>
                                <Select.Option value="Consignee">
                                  Consignee
                                </Select.Option>
                                <Select.Option value="Contract Party">
                                  Contract Party
                                </Select.Option>
                                <Select.Option value="Forwarder">
                                  Forwarder
                                </Select.Option>
                                <Select.Option value="Main Notify Party">
                                  Main Notify Party
                                </Select.Option>
                                <Select.Option value="First Additional">
                                  First Additional Notify Party
                                </Select.Option>
                                <Select.Option value="Second Additional">
                                  Second Additional Notify Party
                                </Select.Option>
                                <Select.Option value="Shipper">
                                  Shipper
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              label="Payment Location"
                              name="paymentLocation"
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      {/* TAB 8 */}
                      {tab.key === "8" && (
                        <>
                          <Form.Item
                            label="Customer Comments"
                            name="customerComment"
                          >
                            <Input.TextArea rows={3} />
                          </Form.Item>
                          <Form.Item
                            label="Partner Email Notifications"
                            name="emailnotifications"
                          >
                            <Input type="email" />
                          </Form.Item>
                        </>
                      )}
                    </Tabs.TabPane>
                  ))}
                </Tabs>

                <Divider />
                <Row justify="space-between" style={{ marginTop: 8 }}>
                  <Col>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={() => window.history.back()}
                      style={{ borderRadius: 20 }}
                    >
                      Voltar
                    </Button>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        type="default"
                        icon={<SaveOutlined />}
                        size="large"
                        style={{
                          background: "#ecf6fe",
                          borderColor: "#0066cc",
                          color: "#0066cc",
                          fontWeight: 500,
                          borderRadius: 24,
                        }}
                        onClick={() => validateAndSubmit(handleSave)}
                      >
                        Salvar
                      </Button>
                      <Button
                        type="primary"
                        icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                        size="large"
                        style={{ fontWeight: 500, borderRadius: 24 }}
                        onClick={() => validateAndSubmit(handleConfirm)}
                      >
                        Confirmar
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            </div>
          </Card>
        </Col>

        {/* Card da direita */}
        <Col xs={24} md={9} xl={8}>
          <Card
            title={
              <span>
                <DollarOutlined style={{ color: "#009688", marginRight: 6 }} />
                <b>Resumo de Custos</b>
              </span>
            }
            bordered={false}
            style={{
              borderRadius: 16,
              background: "#fcfcfc",
              boxShadow: "0 2px 12px #0066cc18",
              marginTop: 8,
            }}
            headStyle={{ background: "#f7fafd", borderRadius: "16px 16px 0 0" }}
          >
            <Text strong type="secondary">
              Ocean Freight
            </Text>
            <Table
              size="small"
              pagination={false}
              style={{ marginTop: 10, marginBottom: 16 }}
              dataSource={freightTaxes.map((tax: any) => ({
                key: tax.category || tax.title,
                category: tax.category || tax.title,
                valor:
                  (Number(tax.value || 0) * (qtdContainers || 1)).toFixed(2) +
                  " " +
                  (tax.currency || ""),
              }))}
              columns={[
                { title: "Categoria", dataIndex: "category" },
                { title: "Valor", dataIndex: "valor", align: "right" as const },
              ]}
            />

            <Text strong type="secondary">
              Taxas Locais
            </Text>
            <Table
              size="small"
              pagination={false}
              style={{ marginTop: 10, marginBottom: 16 }}
              dataSource={originTaxes.map((tax: any) => ({
                key: tax.category || tax.title,
                category: tax.category || tax.title,
                valor:
                  (tax.applicability === "C" || !tax.applicability
                    ? Number(tax.value || 0) * (qtdContainers || 1)
                    : Number(tax.value || 0)) +
                  " " +
                  (tax.currency || ""),
              }))}
              columns={[
                { title: "Categoria", dataIndex: "category" },
                { title: "Valor", dataIndex: "valor", align: "right" as const },
              ]}
            />

            <Divider style={{ margin: "16px 0" }} />
            <Title level={5} style={{ marginBottom: 4 }}>
              <DollarOutlined style={{ color: "#0066cc", marginRight: 6 }} />
              Total Frete a Pagar
            </Title>
            <Text strong style={{ fontSize: 22 }}>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(
                freightTaxes.reduce(
                  (acc: number, t: any) =>
                    acc + Number(t.value || 0) * (qtdContainers || 1),
                  0
                )
              )}
              <span style={{ color: "#999", fontSize: 16 }}> + taxas</span>
            </Text>
            <Divider style={{ margin: "16px 0" }} />
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              <InfoCircleOutlined
                style={{ color: "#009688", marginRight: 6 }}
              />
              Revise todos os valores antes de finalizar o booking.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      {/* Modal feedback */}
      <Modal
        open={modal.visible}
        title={modal.title}
        onCancel={() => setModal({ ...modal, visible: false })}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setModal({ ...modal, visible: false })}
            style={{ borderRadius: 20 }}
          >
            Fechar
          </Button>,
        ]}
        centered
      >
        <Text>{modal.message}</Text>
      </Modal>
    </div>
  );
};

// Relacione os campos a cada tab
const FIELDS_BY_TAB: Record<string, string[]> = {
  "1": ["carrier", "contractNumber", "bookingOffice"],
  "2": ["shipper", "forwarder", "consignee"],
  "3": ["shipperRefNumber", "forwardRefNumber", "purchaseOrderNumber"],
  "4": [
    "moveType",
    "placeCarrierReceipt",
    "earlyDepartureDate",
    "placeCarrierDelivery",
    "latestDeliveryDate",
  ],
  "5": ["qtdContainers", "tipo_container"],
  "6": ["mercadoria"],
  "7": ["paymentChargeType", "paymentTerm", "payer", "paymentLocation"],
  "8": ["customerComment", "emailnotifications"],
};

export default InfoPedido;
